import {
	POLARIZATION_TOLERANCE,
	parseRatingDistribution,
	type RatingDistribution as RatingDistributionData,
} from '#app/scoring/scoring-model.ts'

/**
 * The star histogram, and what the average is hiding.
 *
 * Google publishes this and shows it as five inert bars. The interpretation is
 * the part worth reading: a 4.5 with 10% one-stars is a different business from
 * a flat 4.5, and that difference is invisible in the number every directory
 * repeats.
 *
 * It is also the shape the scoring model already reacts to — the polarization
 * penalty keys off exactly this — so showing it lets a reader see the same
 * evidence the score saw rather than taking the score on faith.
 *
 * Present on every published business: ratingDist has 100% coverage, which
 * makes this one of the few genuinely universal things a listing can show.
 *
 * Widths, not heights. A percentage height needs a parent with a resolved
 * height; a bar's width resolves against its container with no such dependency.
 */

/** Stars, high to low — the order Google uses and readers expect. */
const STARS = [5, 4, 3, 2, 1] as const

export function RatingDistribution({
	ratingDist,
	ratingValue,
	ratingCount,
}: {
	ratingDist: RatingDistributionData | null
	ratingValue: number | null
	ratingCount: number | null
}) {
	const dist = parseRatingDistribution(ratingDist)
	if (!dist) return null

	const total = STARS.reduce((sum, star) => sum + (dist[star] ?? 0), 0)
	if (total === 0) return null

	const lowStars = (dist[1] ?? 0) + (dist[2] ?? 0)
	const lowShare = lowStars / total
	// The same threshold the score's polarization penalty uses, so the sentence
	// and the model cannot disagree about what counts as unusual.
	const isPolarized = lowShare > POLARIZATION_TOLERANCE

	return (
		<section className="mb-10">
			<h2 className="mb-1 font-serif text-xl tracking-tight">
				How the reviews break down
			</h2>
			<p className="text-muted-foreground mb-5 font-mono text-xs tabular-nums">
				{ratingValue?.toFixed(1)}★ average from{' '}
				{(ratingCount ?? total).toLocaleString()} Google reviews
			</p>

			<ul className="mb-4 space-y-1.5">
				{STARS.map((star) => {
					const count = dist[star] ?? 0
					const share = count / total
					return (
						<li key={star} className="flex items-center gap-3">
							<span className="text-muted-foreground w-8 shrink-0 font-mono text-xs tabular-nums">
								{star}★
							</span>
							<div className="bg-muted h-2 flex-1 overflow-hidden rounded-full">
								<div
									className={
										star <= 2 ? 'bg-destructive/70 h-full' : 'bg-foreground/70 h-full'
									}
									style={{ width: `${share * 100}%` }}
								/>
							</div>
							<span className="text-muted-foreground w-20 shrink-0 text-right font-mono text-xs tabular-nums">
								{count.toLocaleString()}
								<span className="text-muted-foreground/60">
									{' '}
									{Math.round(share * 100)}%
								</span>
							</span>
						</li>
					)
				})}
			</ul>

			{isPolarized ? (
				<p className="text-muted-foreground max-w-2xl text-sm leading-relaxed">
					{Math.round(lowShare * 100)}% of these reviews are one or two stars.
					A high average with a tail like that usually means something specific
					goes wrong for a minority of customers, which is what the cautions
					below are drawn from.
				</p>
			) : (
				<p className="text-muted-foreground max-w-2xl text-sm leading-relaxed">
					Low ratings are rare here — {Math.round(lowShare * 100)}% of reviews
					are one or two stars.
				</p>
			)}
		</section>
	)
}
